import { BASE_PATH } from '@/constants/config';
import { isChunkLoadError } from '@/lib/analytics/adapters/noisy-client-exceptions';

/**
 * Survives the reload so a still-missing asset cannot loop. Short-lived so a
 * later deploy in the same tab can recover again.
 */
const STORAGE_KEY = 'respondent:stale-chunk-reload';
const RELOAD_COOLDOWN_MS = 30_000;
const RELOAD_DEBOUNCE_MS = 50;

let pendingReload = false;
let reloadTimer: ReturnType<typeof setTimeout> | undefined;
let listenersInstalled = false;
let reloadPage = () => {
  globalThis.location.reload();
};
let now = () => Date.now();

/**
 * True when a chunk/module load failure should trigger a one-shot reload.
 * False when we already tried recently, storage cannot persist the guard
 * (reload would loop), or this is SSR.
 */
export function canAttemptStaleChunkReload(): boolean {
  if (pendingReload) return false;
  if (globalThis.window === undefined) return false;
  const attempt = readAttemptTimestamp();
  if (attempt.status === 'unavailable') return false;
  if (attempt.timestamp === null) return true;
  return now() - attempt.timestamp >= RELOAD_COOLDOWN_MS;
}

/**
 * If `error` is a stale hashed-asset / dynamic-import failure, schedule at
 * most one full page reload so the browser fetches the current manifest.
 * Returns whether a reload is in flight (including one already scheduled).
 */
export function recoverFromStaleChunkLoad(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  return scheduleOneShotReload();
}

/**
 * Vite `vite:preloadError`, `unhandledrejection`, and hashed `/assets/*`
 * script/link failures. Idempotent. No-ops during SSR.
 */
export function installStaleChunkRecovery(): void {
  if (listenersInstalled) return;
  if (globalThis.window === undefined) return;
  listenersInstalled = true;

  globalThis.window.addEventListener('vite:preloadError', onVitePreloadError);
  globalThis.window.addEventListener(
    'unhandledrejection',
    onUnhandledRejection,
  );
  globalThis.window.addEventListener('error', onWindowError, true);
}

function onVitePreloadError(event: Event): void {
  const payload = getVitePreloadPayload(event);
  const handled =
    (payload !== undefined && recoverFromStaleChunkLoad(payload)) ||
    scheduleOneShotReload();
  // Vite rethrows the preload failure unless this is canceled. Only suppress
  // it when a reload is actually scheduled; otherwise the router error
  // boundary (Oops / Try again) must still render.
  if (handled) event.preventDefault();
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  if (recoverFromStaleChunkLoad(event.reason)) {
    event.preventDefault();
  }
}

function onWindowError(event: Event): void {
  if (isHashedAppAssetTarget(event.target)) {
    scheduleOneShotReload();
    return;
  }
  if (!(event instanceof ErrorEvent)) return;
  recoverFromStaleChunkLoad(event.error ?? event.message);
}

function scheduleOneShotReload(): boolean {
  if (pendingReload) return true;
  if (!canAttemptStaleChunkReload()) return false;
  if (!writeAttemptTimestamp()) return false;

  pendingReload = true;
  reloadTimer = globalThis.setTimeout(() => {
    reloadPage();
  }, RELOAD_DEBOUNCE_MS);
  return true;
}

function readAttemptTimestamp():
  | { status: 'unavailable' }
  | { status: 'ok'; timestamp: number | null } {
  try {
    const storage = globalThis.sessionStorage;
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return { status: 'ok', timestamp: null };
    const timestamp = Number(raw);
    if (!Number.isFinite(timestamp)) {
      return { status: 'ok', timestamp: null };
    }
    return { status: 'ok', timestamp };
  } catch {
    return { status: 'unavailable' };
  }
}

function writeAttemptTimestamp(): boolean {
  try {
    globalThis.sessionStorage.setItem(STORAGE_KEY, String(now()));
    return true;
  } catch {
    return false;
  }
}

function getVitePreloadPayload(event: Event): unknown {
  if (!('payload' in event)) return undefined;
  return event.payload;
}

function isHashedAppAssetTarget(target: EventTarget | null): boolean {
  if (target instanceof HTMLScriptElement) {
    return isHashedAppAssetUrl(target.src);
  }
  if (target instanceof HTMLLinkElement) {
    return isHashedAppAssetUrl(target.href);
  }
  return false;
}

function isHashedAppAssetUrl(url: string): boolean {
  if (url.length === 0) return false;
  try {
    const { pathname } = new URL(
      url,
      globalThis.location?.origin ?? 'https://app.respondent.io',
    );
    const path = pathname.toLowerCase();
    const assetsPrefix = `${BASE_PATH}/assets/`;
    if (!path.startsWith(assetsPrefix)) return false;
    const fileName = path.slice(assetsPrefix.length);
    if (fileName.length === 0 || fileName.includes('/')) return false;
    return (
      fileName.endsWith('.js') ||
      fileName.endsWith('.mjs') ||
      fileName.endsWith('.css')
    );
  } catch {
    return false;
  }
}

/**
 * Test-only: swap reload/clock and clear in-memory state. Pass
 * `preserveAttempt` to keep the sessionStorage cooldown flag (simulates a
 * page that just reloaded after recovery).
 */
export function __resetStaleChunkRecoveryForTesting(overrides?: {
  reload?: () => void;
  now?: () => number;
  preserveAttempt?: boolean;
}): void {
  if (reloadTimer !== undefined) {
    globalThis.clearTimeout(reloadTimer);
    reloadTimer = undefined;
  }
  pendingReload = false;
  reloadPage = overrides?.reload ?? (() => globalThis.location.reload());
  now = overrides?.now ?? (() => Date.now());

  if (listenersInstalled && globalThis.window !== undefined) {
    globalThis.window.removeEventListener(
      'vite:preloadError',
      onVitePreloadError,
    );
    globalThis.window.removeEventListener(
      'unhandledrejection',
      onUnhandledRejection,
    );
    globalThis.window.removeEventListener('error', onWindowError, true);
  }
  listenersInstalled = false;

  if (overrides?.preserveAttempt) return;
  try {
    globalThis.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // jsdom always has sessionStorage; ignore blocked-storage hosts.
  }
}
